import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, FileText, Receipt, Briefcase, Layers, Users, ArrowRight, X
} from 'lucide-react';
import apiClient from '../api/client';

const CATEGORY_CONFIG = {
  invoices:   { label: 'Invoices',    icon: FileText,  color: 'text-blue-500',   bg: 'bg-blue-50' },
  expenses:   { label: 'Expenses',    icon: Receipt,   color: 'text-purple-500', bg: 'bg-purple-50' },
  vendors:    { label: 'Vendors',     icon: Briefcase, color: 'text-green-500',  bg: 'bg-green-50' },
  glAccounts: { label: 'GL Accounts', icon: Layers,    color: 'text-amber-500',  bg: 'bg-amber-50' },
  users:      { label: 'Users',       icon: Users,     color: 'text-cyan-500',   bg: 'bg-cyan-50' },
  pages:      { label: 'Pages',       icon: ArrowRight,color: 'text-gray-500',   bg: 'bg-gray-50' },
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Ctrl+K to open/focus
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        setResults(null);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await apiClient.get('/search/global', { params: { q } });
      setResults(res.data);
      setSelectedIdx(0);
    } catch { setResults(null); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  // Build flat list for keyboard navigation
  const flatItems = [];
  if (results) {
    for (const cat of ['pages', 'invoices', 'expenses', 'vendors', 'glAccounts', 'users']) {
      const items = results[cat] || [];
      if (items.length > 0) {
        items.forEach(item => flatItems.push({ cat, item }));
      }
    }
  }

  const handleSelect = (cat, item) => {
    setOpen(false);
    setQuery('');
    setResults(null);
    switch (cat) {
      case 'pages':
        navigate(item.path);
        break;
      case 'invoices':
        navigate(`/all-documents?search=${encodeURIComponent(item.InvoiceNumber || item.InvoiceID)}`);
        break;
      case 'expenses':
        navigate(`/expenses?search=${encodeURIComponent(item.ExpenseNumber || item.ExpenseID)}`);
        break;
      case 'vendors':
        navigate(`/vendors?search=${encodeURIComponent(item.VendorName)}`);
        break;
      case 'glAccounts':
        navigate(`/gl-accounts?search=${encodeURIComponent(item.AccountCode)}`);
        break;
      case 'users':
        navigate(`/users?search=${encodeURIComponent(item.Email)}`);
        break;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatItems[selectedIdx]) {
      e.preventDefault();
      handleSelect(flatItems[selectedIdx].cat, flatItems[selectedIdx].item);
    }
  };

  const fmt = (v, currency = 'GBP') => {
    const n = parseFloat(v) || 0;
    const sym = { GBP: '£', USD: '$', EUR: '€' }[currency] || currency + ' ';
    return sym + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const renderItem = (cat, item, idx) => {
    const cfg = CATEGORY_CONFIG[cat];
    const Icon = cfg.icon;
    const isSelected = idx === selectedIdx;

    let primary = '', secondary = '', badge = '';
    switch (cat) {
      case 'pages':
        primary = item.name;
        secondary = item.path;
        break;
      case 'invoices':
        primary = item.InvoiceNumber || 'No Number';
        secondary = item.VendorName || '';
        badge = `${fmt(item.TotalAmount, item.Currency)} · ${item.Status}`;
        break;
      case 'expenses':
        primary = item.ExpenseNumber || item.Purpose || 'Expense';
        secondary = [item.EmployeeName, item.EmployeeEmail].filter(Boolean).join(' · ');
        badge = `${fmt(item.TotalAmount, item.Currency)} · ${item.Status}`;
        break;
      case 'vendors':
        primary = item.VendorName;
        secondary = [item.ContactEmail, item.TaxID, item.InvoiceCount ? `${item.InvoiceCount} invoices` : ''].filter(Boolean).join(' · ');
        break;
      case 'glAccounts':
        primary = `${item.AccountCode} — ${item.AccountName}`;
        secondary = item.AccountType || '';
        break;
      case 'users':
        primary = `${item.FirstName} ${item.LastName}`;
        secondary = item.Email;
        badge = item.Status;
        break;
    }

    return (
      <button
        key={`${cat}-${idx}`}
        onClick={() => handleSelect(cat, item)}
        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
          isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
        }`}
      >
        <div className={`w-7 h-7 rounded-md ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{primary}</p>
          {secondary && <p className="text-xs text-gray-400 truncate">{secondary}</p>}
        </div>
        {badge && (
          <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">{badge}</span>
        )}
      </button>
    );
  };

  // Count total results
  const totalResults = flatItems.length;
  const hasResults = results && totalResults > 0;
  const noResults = results && totalResults === 0 && query.length >= 2;

  return (
    <div className="relative" ref={containerRef}>
      {/* Search trigger */}
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all duration-200 border ${
          open
            ? 'bg-white dark:bg-gray-700 border-blue-400 ring-2 ring-blue-500/20 w-72 shadow-md'
            : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm w-56 cursor-pointer'
        }`}
        onClick={() => { if (!open) { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); } }}
      >
        <Search className={`w-4 h-4 flex-shrink-0 transition-colors ${open ? 'text-blue-500' : 'text-gray-400'}`} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          placeholder="Search anything..."
          className="flex-1 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 outline-none bg-transparent min-w-0"
        />
        {query ? (
          <button
            onClick={(e) => { e.stopPropagation(); setQuery(''); setResults(null); inputRef.current?.focus(); }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <kbd className="ml-auto flex-shrink-0 text-[10px] text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 px-1.5 py-0.5 rounded font-mono leading-none shadow-sm">Ctrl K</kbd>
        )}
      </div>

      {/* Dropdown results panel */}
      {open && (
        <div className="absolute top-full left-0 mt-1.5 w-[440px] bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && (
              <div className="px-4 py-6 text-center text-sm text-gray-400">Searching...</div>
            )}

            {!loading && noResults && (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                No results for "<span className="font-medium text-gray-600">{query}</span>"
              </div>
            )}

            {!loading && hasResults && (
              <div className="py-1">
                {(() => {
                  let globalIdx = 0;
                  return ['pages', 'invoices', 'expenses', 'vendors', 'glAccounts', 'users'].map(cat => {
                    const items = results[cat] || [];
                    if (items.length === 0) return null;
                    const cfg = CATEGORY_CONFIG[cat];
                    return (
                      <div key={cat}>
                        <div className="px-3 pt-2 pb-1">
                          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{cfg.label}</span>
                        </div>
                        {items.map(item => {
                          const idx = globalIdx++;
                          return renderItem(cat, item, idx);
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {!loading && !results && (
              <div className="px-4 py-4 text-center text-sm text-gray-400">
                Type at least 2 characters to search
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><kbd className="bg-gray-100 px-1 py-0.5 rounded font-mono">↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="bg-gray-100 px-1 py-0.5 rounded font-mono">↵</kbd> Open</span>
            <span className="flex items-center gap-1"><kbd className="bg-gray-100 px-1 py-0.5 rounded font-mono">ESC</kbd> Close</span>
            {totalResults > 0 && <span className="ml-auto">{totalResults} result{totalResults !== 1 ? 's' : ''}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
